import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Image, ChevronDown, ChevronUp, Download, RefreshCw, 
  UserPlus, FileUp, Check, AlertTriangle, ExternalLink
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AdminRecovery = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedArtist, setExpandedArtist] = useState(null);
  const [importDialog, setImportDialog] = useState(null);
  const [importForm, setImportForm] = useState({});
  const [importing, setImporting] = useState(false);
  const [artists, setArtists] = useState([]);
  const [dbAudit, setDbAudit] = useState(null);
  const [allDbs, setAllDbs] = useState(null);
  const [activeTab, setActiveTab] = useState("media");
  const [deepScanData, setDeepScanData] = useState(null);
  const [deepScanLoading, setDeepScanLoading] = useState(false);
  const [backups, setBackups] = useState([]);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [prodRestoring, setProdRestoring] = useState(false);
  const [prodRestoreResult, setProdRestoreResult] = useState(null);

  const restoreProductionData = async () => {
    if (!window.confirm("This will import old production data (real artists + submissions) from the backup. Existing records will NOT be overwritten. Continue?")) return;
    setProdRestoring(true);
    setProdRestoreResult(null);
    try {
      const res = await axios.post(`${API}/admin/restore-production`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000,
      });
      setProdRestoreResult(res.data);
      toast.success(`Restored ${res.data.users_imported} users + ${res.data.submissions_imported} submissions`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    } finally {
      setProdRestoring(false);
    }
  };

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/recovery/cloudinary-media`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (error) {
      toast.error("Failed to fetch Cloudinary media");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchArtists = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/artists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArtists(response.data);
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchMedia();
    fetchArtists();
    fetchDbInfo();
    fetchBackups();
  }, [fetchMedia, fetchArtists]);

  const fetchBackups = async () => {
    try {
      const response = await axios.get(`${API}/admin/backup/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBackups(response.data);
    } catch {}
  };

  const createBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await axios.post(`${API}/admin/backup/create`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Backup created: ${response.data.backup_id}`);
      fetchBackups();
    } catch (error) {
      toast.error("Backup failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setBackupLoading(false);
    }
  };

  const restoreBackup = async (backupId) => {
    if (!window.confirm(`Are you sure you want to restore from backup "${backupId}"? A safety backup will be created first.`)) return;
    setRestoring(backupId);
    try {
      const response = await axios.post(`${API}/admin/backup/${backupId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Restored! Users: ${response.data.restored.users}, Submissions: ${response.data.restored.submissions}`);
      fetchBackups();
    } catch (error) {
      toast.error("Restore failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setRestoring(null);
    }
  };

  const fetchDbInfo = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [auditRes, dbsRes] = await Promise.all([
        axios.get(`${API}/admin/recovery/db-inspector`, { headers }).catch(() => null),
        axios.get(`${API}/diagnostic/all-databases`, { headers }).catch(() => null),
      ]);
      if (auditRes) setDbAudit(auditRes.data);
      if (dbsRes) setAllDbs(dbsRes.data);
    } catch {}
  };

  const runDeepScan = async () => {
    setDeepScanLoading(true);
    try {
      const response = await axios.get(`${API}/admin/recovery/deep-scan`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeepScanData(response.data);
      toast.success("Deep scan complete!");
    } catch (error) {
      toast.error("Deep scan failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setDeepScanLoading(false);
    }
  };

  const openImportDialog = (group) => {
    setImportDialog(group);
    setImportForm({
      artist_id: group.artist_id,
      artist_name: group.artist_name || "",
      city: "",
      venue: "",
      date: group.files[0]?.created_at?.slice(0, 10) || "",
      s_user_id: "",
      status: "draft",
      merchandise_url: "",
      proof_of_play_url: "",
      bottle_console_url: "",
      branding_stories_post_url: "",
      instagram_analytics_url: "",
      instagram_analytics_impressions: 0,
      track_play_count: 0,
    });
  };

  const assignFileToField = (url, field) => {
    setImportForm((prev) => ({ ...prev, [field]: url }));
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const payload = { ...importForm };
      // Clean empty strings to null
      for (const key of ["merchandise_url", "proof_of_play_url", "bottle_console_url", "branding_stories_post_url", "instagram_analytics_url"]) {
        if (!payload[key]) payload[key] = null;
      }
      await axios.post(`${API}/admin/recovery/import-submission`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Submission imported successfully!");
      setImportDialog(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import");
    } finally {
      setImporting(false);
    }
  };

  const handleCreatePlaceholder = async (group) => {
    try {
      const formData = new FormData();
      formData.append("artist_id", group.artist_id);
      formData.append("name", `Recovered Artist (${group.artist_id.slice(0, 8)})`);
      formData.append("email", `recovered-${group.artist_id.slice(0, 8)}@placeholder.local`);
      await axios.post(`${API}/admin/recovery/create-placeholder-artist`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Placeholder artist created");
      fetchMedia();
      fetchArtists();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create placeholder");
    }
  };

  const mediaFields = [
    { key: "merchandise_url", label: "Merchandise Photo" },
    { key: "proof_of_play_url", label: "Proof of Play" },
    { key: "bottle_console_url", label: "Bottle Console" },
    { key: "branding_stories_post_url", label: "Branding/Stories" },
    { key: "instagram_analytics_url", label: "Instagram Analytics" },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20" data-testid="recovery-loading">
          <RefreshCw className="animate-spin text-[#00B4E5]" size={32} />
          <span className="ml-3 text-gray-500">Scanning Cloudinary for recoverable media...</span>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto" data-testid="admin-recovery">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="text-amber-500" size={28} />
            <h1 className="font-heading text-3xl text-gray-900">DATA RECOVERY</h1>
          </div>
          <p className="text-gray-500">
            Found <strong>{data?.total_files || 0}</strong> media files across{" "}
            <strong>{data?.artist_groups?.length || 0}</strong> artist accounts in Cloudinary.
            Assign files to submissions to recover your data.
          </p>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-gray-200 p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wider">Total Files</p>
            <p className="text-2xl font-heading text-gray-900">{data?.total_files || 0}</p>
          </div>
          <div className="bg-white border border-gray-200 p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wider">Artist Groups</p>
            <p className="text-2xl font-heading text-gray-900">{data?.artist_groups?.length || 0}</p>
          </div>
          <div className="bg-white border border-gray-200 p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wider">Missing Artists</p>
            <p className="text-2xl font-heading text-red-600">
              {data?.artist_groups?.filter((g) => !g.artist_exists).length || 0}
            </p>
          </div>
        </div>

        {/* Production Restore Banner */}
        <div className="mb-6 bg-amber-50 border-2 border-amber-400 p-5 rounded" data-testid="restore-production-banner">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-lg text-amber-800 flex items-center gap-2">
                <Download size={20} /> RESTORE OLD PRODUCTION DATA
              </h2>
              <p className="text-sm text-amber-700 mt-1">
                Import 12 real artist submissions + 9 user accounts from the old kingfisher-campaign database backup.
                Existing records will not be overwritten. Safe to run multiple times.
              </p>
            </div>
            <Button
              onClick={restoreProductionData}
              disabled={prodRestoring}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-6 shrink-0"
              data-testid="restore-production-btn"
            >
              {prodRestoring ? <><RefreshCw size={16} className="animate-spin mr-2" /> Restoring...</> : "Restore Now"}
            </Button>
          </div>
          {prodRestoreResult && (
            <div className="mt-3 bg-white border border-amber-200 p-3 rounded text-sm">
              <p className="font-semibold text-green-700">
                Restored {prodRestoreResult.users_imported} users, {prodRestoreResult.submissions_imported} submissions, {prodRestoreResult.notifications_imported} notifications
              </p>
              {prodRestoreResult.users_updated > 0 && (
                <p className="text-amber-700">Updated {prodRestoreResult.users_updated} existing users with missing fields</p>
              )}
              <p className="text-gray-500 mt-1">
                Final DB: {prodRestoreResult.final_counts?.users} users, {prodRestoreResult.final_counts?.submissions} submissions
              </p>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("media")}
            className={`px-4 py-2 text-sm font-semibold uppercase tracking-wider border-b-2 transition ${
              activeTab === "media" ? "border-[#00B4E5] text-[#00B4E5]" : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            Cloudinary Media ({data?.total_files || 0})
          </button>
          <button
            onClick={() => setActiveTab("db")}
            className={`px-4 py-2 text-sm font-semibold uppercase tracking-wider border-b-2 transition ${
              activeTab === "db" ? "border-[#00B4E5] text-[#00B4E5]" : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            Database Inspector
          </button>
          <button
            onClick={() => setActiveTab("deep")}
            className={`px-4 py-2 text-sm font-semibold uppercase tracking-wider border-b-2 transition ${
              activeTab === "deep" ? "border-[#ED5A34] text-[#ED5A34]" : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            Deep Recovery Scan
          </button>
          <button
            onClick={() => setActiveTab("backups")}
            className={`px-4 py-2 text-sm font-semibold uppercase tracking-wider border-b-2 transition ${
              activeTab === "backups" ? "border-green-600 text-green-600" : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            Backups ({backups.length})
          </button>
        </div>

        {activeTab === "db" && (
          <div className="space-y-6 mb-8">
            {/* All Databases */}
            {allDbs && (
              <div className="bg-white border border-gray-200 p-5">
                <h2 className="font-heading text-xl mb-4 text-gray-900">ALL DATABASES</h2>
                <div className="space-y-4">
                  {Object.entries(allDbs).map(([dbName, cols]) => (
                    <div key={dbName} className="border border-gray-100 p-3">
                      <h3 className="font-semibold text-gray-800 mb-2">{dbName}</h3>
                      {typeof cols === 'object' && !Array.isArray(cols) ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          {Object.entries(cols).map(([colName, val]) => (
                            !colName.endsWith('_samples') && (
                              <div key={colName} className="bg-gray-50 p-2 rounded">
                                <p className="text-xs text-gray-500">{colName}</p>
                                <p className="text-lg font-semibold text-gray-900">{typeof val === 'number' ? val : '—'}</p>
                              </div>
                            )
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">No collections</p>
                      )}
                      {/* Show samples if available */}
                      {typeof cols === 'object' && Object.entries(cols)
                        .filter(([k, v]) => k.endsWith('_samples') && Array.isArray(v) && v.length > 0)
                        .map(([k, samples]) => (
                          <div key={k} className="mt-3">
                            <p className="text-xs font-semibold text-amber-600 uppercase mb-1">
                              Old Data Found in {k.replace('_samples', '')}:
                            </p>
                            <div className="bg-amber-50 border border-amber-200 p-2 rounded overflow-x-auto">
                              <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                                {JSON.stringify(samples, null, 2)}
                              </pre>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Data Audit */}
            {dbAudit && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Users Audit */}
                <div className="bg-white border border-gray-200 p-5">
                  <h2 className="font-heading text-xl mb-4 text-gray-900">USERS AUDIT</h2>
                  <div className="space-y-3">
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-gray-500">Total Users</span>
                      <span className="font-semibold">{dbAudit.users?.total_in_db}</span>
                    </div>
                    {dbAudit.users?.by_role && Object.entries(dbAudit.users.by_role).map(([role, count]) => (
                      <div key={role} className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-gray-500 pl-4">Role: {role}</span>
                        <span className="font-semibold">{count}</span>
                      </div>
                    ))}
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-gray-500">Missing ID field</span>
                      <span className={`font-semibold ${dbAudit.users?.missing_id_field > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {dbAudit.users?.missing_id_field}
                      </span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-gray-500">Missing Email</span>
                      <span className={`font-semibold ${dbAudit.users?.missing_email > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {dbAudit.users?.missing_email}
                      </span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-gray-500">Missing Created At</span>
                      <span className={`font-semibold ${dbAudit.users?.missing_created_at > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {dbAudit.users?.missing_created_at}
                      </span>
                    </div>
                  </div>
                  {dbAudit.users?.problematic_users?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs font-semibold text-red-600 uppercase mb-2">Problematic Users:</p>
                      <div className="bg-red-50 border border-red-200 p-2 rounded overflow-x-auto">
                        <pre className="text-xs">{JSON.stringify(dbAudit.users.problematic_users, null, 2)}</pre>
                      </div>
                    </div>
                  )}
                </div>

                {/* Submissions Audit */}
                <div className="bg-white border border-gray-200 p-5">
                  <h2 className="font-heading text-xl mb-4 text-gray-900">SUBMISSIONS AUDIT</h2>
                  <div className="space-y-3">
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-gray-500">Total Submissions</span>
                      <span className="font-semibold">{dbAudit.submissions?.total_in_db}</span>
                    </div>
                    {dbAudit.submissions?.by_status && Object.entries(dbAudit.submissions.by_status).map(([status, count]) => (
                      <div key={status} className="flex justify-between py-1 border-b border-gray-100">
                        <span className="text-gray-500 pl-4">Status: {status}</span>
                        <span className="font-semibold">{count}</span>
                      </div>
                    ))}
                    <div className="flex justify-between py-1 border-b border-gray-100">
                      <span className="text-gray-500">Missing ID field</span>
                      <span className={`font-semibold ${dbAudit.submissions?.missing_id_field > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {dbAudit.submissions?.missing_id_field}
                      </span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-gray-500">Missing Artist ID</span>
                      <span className={`font-semibold ${dbAudit.submissions?.missing_artist_id > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {dbAudit.submissions?.missing_artist_id}
                      </span>
                    </div>
                  </div>
                  {dbAudit.submissions?.problematic_submissions?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs font-semibold text-red-600 uppercase mb-2">Problematic Submissions:</p>
                      <div className="bg-red-50 border border-red-200 p-2 rounded overflow-x-auto">
                        <pre className="text-xs">{JSON.stringify(dbAudit.submissions.problematic_submissions, null, 2)}</pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!allDbs && !dbAudit && (
              <div className="bg-amber-50 border border-amber-200 p-6 text-center">
                <AlertTriangle className="mx-auto text-amber-500 mb-3" size={32} />
                <p className="text-amber-800 font-semibold">Database Inspector loading...</p>
                <p className="text-amber-600 text-sm mt-1">If this stays empty, the diagnostic endpoints may not be deployed yet. Redeploy to enable.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === "media" && (
        <>
        {/* Artist groups */}
        <div className="space-y-4">
          {data?.artist_groups?.map((group) => (
            <div
              key={group.artist_id}
              className="bg-white border border-gray-200 overflow-hidden"
              data-testid={`recovery-group-${group.artist_id.slice(0, 8)}`}
            >
              {/* Group header */}
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition"
                onClick={() =>
                  setExpandedArtist(expandedArtist === group.artist_id ? null : group.artist_id)
                }
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`w-10 h-10 flex items-center justify-center font-heading text-sm ${
                      group.artist_exists
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-600"
                    }`}
                  >
                    {group.artist_exists ? <Check size={20} /> : "?"}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {group.artist_name || `Unknown Artist (${group.artist_id.slice(0, 8)}...)`}
                    </p>
                    <p className="text-sm text-gray-500">
                      {group.file_count} files | {group.date_range}
                      {group.artist_email && ` | ${group.artist_email}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!group.artist_exists && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCreatePlaceholder(group);
                      }}
                      className="text-amber-600 border-amber-300"
                      data-testid={`create-placeholder-${group.artist_id.slice(0, 8)}`}
                    >
                      <UserPlus size={16} className="mr-1" /> Create Placeholder
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      openImportDialog(group);
                    }}
                    className="text-[#00B4E5] border-[#00B4E5]/30"
                    data-testid={`import-submission-${group.artist_id.slice(0, 8)}`}
                  >
                    <FileUp size={16} className="mr-1" /> Import as Submission
                  </Button>
                  {expandedArtist === group.artist_id ? (
                    <ChevronUp size={20} className="text-gray-400" />
                  ) : (
                    <ChevronDown size={20} className="text-gray-400" />
                  )}
                </div>
              </div>

              {/* Expanded files */}
              {expandedArtist === group.artist_id && (
                <div className="border-t border-gray-200 p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {group.files.map((file, idx) => (
                      <div
                        key={idx}
                        className="group relative border border-gray-200 rounded overflow-hidden"
                      >
                        <a href={file.url} target="_blank" rel="noreferrer">
                          <img
                            src={file.url}
                            alt={`File ${idx + 1}`}
                            className="w-full h-32 object-cover"
                            loading="lazy"
                          />
                        </a>
                        <div className="p-2">
                          <p className="text-[10px] text-gray-400 truncate">
                            {file.created_at?.slice(0, 10)}
                          </p>
                          <p className="text-[10px] text-gray-400 truncate">{file.format}</p>
                        </div>
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          className="absolute top-1 right-1 bg-black/50 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition"
                        >
                          <ExternalLink size={12} />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        </>
        )}

        {activeTab === "deep" && (
          <div className="space-y-6 mb-8">
            {/* Scan Button */}
            <div className="bg-white border border-gray-200 p-6 text-center">
              <h2 className="font-heading text-2xl text-gray-900 mb-3">DEEP RECOVERY SCAN</h2>
              <p className="text-gray-500 mb-4 max-w-xl mx-auto">
                Scans MongoDB server info, all databases, WiredTiger data files, oplog, and notifications 
                for any trace of deleted data. This may take a few seconds.
              </p>
              <Button
                onClick={runDeepScan}
                disabled={deepScanLoading}
                className="bg-[#ED5A34] hover:bg-[#d44a28] text-white px-8 py-3 text-lg"
                data-testid="run-deep-scan"
              >
                {deepScanLoading ? (
                  <><RefreshCw className="animate-spin mr-2" size={20} /> Scanning...</>
                ) : (
                  <><Download className="mr-2" size={20} /> Run Deep Scan</>
                )}
              </Button>
            </div>

            {deepScanData && (
              <>
                {/* Recommendations */}
                {deepScanData.recovery_recommendations?.length > 0 && (
                  <div className="bg-amber-50 border border-amber-300 p-5">
                    <h3 className="font-heading text-lg text-amber-800 mb-3">RECOVERY RECOMMENDATIONS</h3>
                    <ul className="space-y-2">
                      {deepScanData.recovery_recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-amber-900">
                          <AlertTriangle size={16} className="text-amber-500 mt-0.5 shrink-0" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* MongoDB Info */}
                <div className="bg-white border border-gray-200 p-5">
                  <h3 className="font-heading text-lg mb-3 text-gray-900">MONGODB SERVER</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-xs text-gray-400 uppercase">Version</p>
                      <p className="font-semibold">{deepScanData.mongo_info?.version || "Unknown"}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-xs text-gray-400 uppercase">Storage Engine</p>
                      <p className="font-semibold">{deepScanData.mongo_info?.storage_engine || "Unknown"}</p>
                    </div>
                    <div className={`p-3 rounded ${deepScanData.mongo_info?.is_atlas ? "bg-green-50" : "bg-gray-50"}`}>
                      <p className="text-xs text-gray-400 uppercase">Connection Type</p>
                      <p className={`font-semibold text-sm ${deepScanData.mongo_info?.is_atlas ? "text-green-700" : ""}`}>
                        {deepScanData.mongo_info?.connection_type || "Unknown"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* All Databases */}
                <div className="bg-white border border-gray-200 p-5">
                  <h3 className="font-heading text-lg mb-3 text-gray-900">ALL DATABASES</h3>
                  <div className="space-y-4">
                    {Object.entries(deepScanData.all_databases || {}).map(([dbName, cols]) => (
                      dbName !== "error" && (
                        <div key={dbName} className="border border-gray-100 p-3">
                          <h4 className="font-semibold text-gray-800 mb-2">{dbName}</h4>
                          {typeof cols === "object" && (
                            <div className="space-y-2">
                              {Object.entries(cols).map(([colName, info]) => (
                                <div key={colName}>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600">{colName}:</span>
                                    <span className="text-sm font-semibold">{info?.count || 0} docs</span>
                                  </div>
                                  {info?.data && info.data.length > 0 && (
                                    <div className="mt-1 bg-gray-50 border border-gray-200 p-2 rounded overflow-x-auto max-h-48 overflow-y-auto">
                                      <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                                        {JSON.stringify(info.data, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    ))}
                  </div>
                </div>

                {/* WiredTiger Recovery */}
                <div className="bg-white border border-gray-200 p-5">
                  <h3 className="font-heading text-lg mb-3 text-gray-900">
                    WIREDTIGER FILE SCAN
                    <span className="text-xs font-normal text-gray-400 ml-2">
                      (Scanning raw data files for deleted records)
                    </span>
                  </h3>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-xs text-gray-400 uppercase">Data Files</p>
                      <p className="font-semibold">{deepScanData.wiredtiger_scan?.data_files || 0}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded">
                      <p className="text-xs text-gray-400 uppercase">Total Size</p>
                      <p className="font-semibold">{deepScanData.wiredtiger_scan?.total_size_mb || 0} MB</p>
                    </div>
                  </div>
                  {deepScanData.wiredtiger_scan?.recovered_strings?.length > 0 ? (
                    <div>
                      <p className="text-sm text-green-700 font-semibold mb-2">
                        Found {deepScanData.wiredtiger_scan.recovered_strings.length} potential data fragments:
                      </p>
                      <div className="bg-green-50 border border-green-200 p-3 rounded max-h-96 overflow-y-auto">
                        {deepScanData.wiredtiger_scan.recovered_strings.map((s, i) => (
                          <div key={i} className={`text-xs py-1 ${i > 0 ? "border-t border-green-100" : ""} ${
                            s.startsWith("EMAIL:") ? "text-blue-700" :
                            s.startsWith("BANK:") ? "text-red-700" :
                            s.startsWith("HANDLE:") ? "text-purple-700" :
                            "text-gray-700"
                          }`}>
                            {s}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">No data fragments found in WiredTiger files.</p>
                  )}
                </div>

                {/* Notifications */}
                {deepScanData.notifications_data?.length > 0 && (
                  <div className="bg-white border border-gray-200 p-5">
                    <h3 className="font-heading text-lg mb-3 text-gray-900">NOTIFICATIONS (may reference old data)</h3>
                    <div className="space-y-2">
                      {deepScanData.notifications_data.map((n, i) => (
                        <div key={i} className="bg-gray-50 p-3 rounded border border-gray-100">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded uppercase">{n.type}</span>
                            <span className="text-sm font-semibold text-gray-800">{n.title}</span>
                          </div>
                          <p className="text-xs text-gray-600">{n.message}</p>
                          <p className="text-[10px] text-gray-400 mt-1">
                            {n.created_at} | User: {n.user_id || "N/A"} | Submission: {n.submission_id || "N/A"}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Journal/Oplog */}
                <div className="bg-white border border-gray-200 p-5">
                  <h3 className="font-heading text-lg mb-3 text-gray-900">JOURNAL / OPLOG</h3>
                  <div className="bg-gray-50 p-3 rounded">
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                      {JSON.stringify(deepScanData.journal_scan, null, 2)}
                    </pre>
                  </div>
                </div>
              </>
            )}

            {!deepScanData && !deepScanLoading && (
              <div className="bg-gray-50 border border-gray-200 p-8 text-center">
                <p className="text-gray-400">Click "Run Deep Scan" to begin scanning for recoverable data.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === "backups" && (
          <div className="space-y-6 mb-8">
            {/* Create Backup */}
            <div className="bg-white border border-gray-200 p-6 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-xl text-gray-900">DATABASE BACKUPS</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Backups are created automatically on every server start and every 6 hours.
                  You can also create manual backups anytime.
                </p>
              </div>
              <Button
                onClick={createBackup}
                disabled={backupLoading}
                className="bg-green-600 hover:bg-green-700 text-white px-6"
                data-testid="create-backup-btn"
              >
                {backupLoading ? (
                  <><RefreshCw className="animate-spin mr-2" size={16} /> Creating...</>
                ) : (
                  <><Download className="mr-2" size={16} /> Create Backup Now</>
                )}
              </Button>
            </div>

            {/* Backup List */}
            {backups.length > 0 ? (
              <div className="bg-white border border-gray-200 overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider">Backup ID</th>
                      <th className="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider">Trigger</th>
                      <th className="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider">Users</th>
                      <th className="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider">Submissions</th>
                      <th className="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider">Notifications</th>
                      <th className="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {backups.map((backup) => (
                      <tr key={backup.backup_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono text-gray-700">{backup.backup_id}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded uppercase font-semibold ${
                            backup.trigger === "startup" ? "bg-blue-100 text-blue-700" :
                            backup.trigger === "scheduled" ? "bg-purple-100 text-purple-700" :
                            backup.trigger === "manual" ? "bg-green-100 text-green-700" :
                            "bg-gray-100 text-gray-700"
                          }`}>
                            {backup.trigger}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {new Date(backup.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold">{backup.stats?.users || 0}</td>
                        <td className="px-4 py-3 text-sm font-semibold">{backup.stats?.submissions || 0}</td>
                        <td className="px-4 py-3 text-sm font-semibold">{backup.stats?.notifications || 0}</td>
                        <td className="px-4 py-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => restoreBackup(backup.backup_id)}
                            disabled={restoring === backup.backup_id}
                            className="text-amber-600 border-amber-300"
                            data-testid={`restore-${backup.backup_id}`}
                          >
                            {restoring === backup.backup_id ? "Restoring..." : "Restore"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 p-8 text-center">
                <p className="text-gray-400">No backups yet. Create one or wait for the next automatic backup.</p>
              </div>
            )}

            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800">
              <p className="font-semibold mb-1">How backups work:</p>
              <ul className="list-disc pl-5 space-y-1 text-blue-700">
                <li>A full backup is created automatically every time the server starts</li>
                <li>Scheduled backups run every 6 hours</li>
                <li>Backups are stored in a separate "backups" database — safe even if the main DB is wiped</li>
                <li>Restoring merges data back (doesn't delete current records)</li>
                <li>A safety backup is always created before any restore operation</li>
              </ul>
            </div>
          </div>
        )}

        {/* Import Dialog */}
        <Dialog open={!!importDialog} onOpenChange={() => setImportDialog(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">IMPORT SUBMISSION</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Metadata */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Submission Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Artist Name *</Label>
                    <Input
                      value={importForm.artist_name || ""}
                      onChange={(e) =>
                        setImportForm((f) => ({ ...f, artist_name: e.target.value }))
                      }
                      data-testid="import-artist-name"
                    />
                  </div>
                  <div>
                    <Label>S-USER ID</Label>
                    <Input
                      value={importForm.s_user_id || ""}
                      onChange={(e) =>
                        setImportForm((f) => ({ ...f, s_user_id: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input
                      value={importForm.city || ""}
                      onChange={(e) =>
                        setImportForm((f) => ({ ...f, city: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <Label>Venue</Label>
                    <Input
                      value={importForm.venue || ""}
                      onChange={(e) =>
                        setImportForm((f) => ({ ...f, venue: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={importForm.date || ""}
                      onChange={(e) =>
                        setImportForm((f) => ({ ...f, date: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select
                      value={importForm.status || "draft"}
                      onValueChange={(v) => setImportForm((f) => ({ ...f, status: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="pending_agency_approval">Pending Agency</SelectItem>
                        <SelectItem value="pending_brand_approval">Pending Brand</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Impressions</Label>
                    <Input
                      type="number"
                      value={importForm.instagram_analytics_impressions || 0}
                      onChange={(e) =>
                        setImportForm((f) => ({
                          ...f,
                          instagram_analytics_impressions: parseInt(e.target.value) || 0,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <Label>Track Plays</Label>
                    <Input
                      type="number"
                      value={importForm.track_play_count || 0}
                      onChange={(e) =>
                        setImportForm((f) => ({
                          ...f,
                          track_play_count: parseInt(e.target.value) || 0,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Media Assignment */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">
                  Assign Media Files
                  <span className="text-xs font-normal text-gray-400 ml-2">
                    Click a file below to assign it to a field
                  </span>
                </h3>

                {mediaFields.map((field) => (
                  <div key={field.key} className="space-y-1">
                    <Label className="flex items-center gap-2">
                      <Image size={14} /> {field.label}
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={importForm[field.key] || ""}
                        onChange={(e) =>
                          setImportForm((f) => ({ ...f, [field.key]: e.target.value }))
                        }
                        placeholder="Paste URL or click a file below"
                        className="text-xs"
                      />
                      {importForm[field.key] && (
                        <a href={importForm[field.key]} target="_blank" rel="noreferrer">
                          <Button variant="outline" size="sm" type="button">
                            <ExternalLink size={14} />
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                ))}

                {/* Clickable thumbnails */}
                <div className="border border-dashed border-gray-300 rounded p-3">
                  <p className="text-xs text-gray-400 mb-2">
                    Click a file to copy its URL, then paste into the field above:
                  </p>
                  <div className="grid grid-cols-6 gap-2">
                    {importDialog?.files?.map((file, idx) => (
                      <div
                        key={idx}
                        className="cursor-pointer border border-gray-200 rounded overflow-hidden hover:ring-2 hover:ring-[#00B4E5] transition"
                        onClick={() => {
                          navigator.clipboard.writeText(file.url);
                          toast.success("URL copied! Paste it into a field above.");
                        }}
                      >
                        <img
                          src={file.url}
                          alt={`File ${idx + 1}`}
                          className="w-full h-16 object-cover"
                          loading="lazy"
                        />
                        <p className="text-[9px] text-center text-gray-400 py-0.5">
                          {file.created_at?.slice(5, 10)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setImportDialog(null)}>
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                className="kf-btn-primary"
                disabled={importing || !importForm.artist_name}
                data-testid="confirm-import"
              >
                {importing ? "Importing..." : "Import Submission"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminRecovery;
